/**
 * Workflow Registry - Maps workflow IDs to their service factories.
 *
 * Provides a centralized way to register and retrieve workflow services.
 * Extensible for adding new workflows (e.g., liver, oncology).
 */

import type { IWorkflowService } from './base-workflow-service';
import type { ServiceContext } from '../core/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Factory function for creating workflow service instances.
 */
export type WorkflowFactory = (context: ServiceContext) => IWorkflowService;

/**
 * Registration entry for a workflow.
 */
export interface WorkflowRegistration {
    /** Unique workflow identifier */
    workflowId: string;
    /** Display name for the workflow */
    name: string;
    /** Factory function to create the service */
    factory: WorkflowFactory;
}

// ============================================================================
// WorkflowRegistry
// ============================================================================

/**
 * Registry for workflow service factories.
 *
 * @example
 * ```typescript
 * // Registration (typically done at app initialization)
 * WorkflowRegistry.register({
 *     workflowId: 'evar',
 *     name: 'EVAR Workflow',
 *     factory: (ctx) => new EvarWorkflowService(ctx),
 * });
 *
 * // Usage
 * const factory = WorkflowRegistry.getFactory('evar');
 * const service = factory(serviceContext);
 * ```
 */
export class WorkflowRegistry {
    private static registrations = new Map<string, WorkflowRegistration>();

    /**
     * Register a workflow service factory.
     *
     * @param registration - Workflow registration info
     * @throws Error if workflow is already registered
     */
    static register(registration: WorkflowRegistration): void {
        if (this.registrations.has(registration.workflowId)) {
            throw new Error(`Workflow already registered: ${registration.workflowId}`);
        }
        this.registrations.set(registration.workflowId, registration);
    }

    /**
     * Get the factory function for a workflow.
     *
     * @param workflowId - The workflow identifier
     * @returns The factory function
     * @throws Error if workflow is not registered
     */
    static getFactory(workflowId: string): WorkflowFactory {
        const registration = this.registrations.get(workflowId);
        if (!registration) {
            throw new Error(
                `Unknown workflow: ${workflowId}. Available: ${this.getRegisteredWorkflowIds().join(', ')}`
            );
        }
        return registration.factory;
    }

    /**
     * Check if a workflow is registered.
     *
     * @param workflowId - The workflow identifier
     * @returns true if registered
     */
    static hasWorkflow(workflowId: string): boolean {
        return this.registrations.has(workflowId);
    }

    /**
     * Get all registered workflow IDs.
     *
     * @returns Array of workflow IDs
     */
    static getRegisteredWorkflowIds(): string[] {
        return Array.from(this.registrations.keys());
    }

    /**
     * Get registration info for a workflow.
     *
     * @param workflowId - The workflow identifier
     * @returns Registration info or undefined
     */
    static getRegistration(workflowId: string): WorkflowRegistration | undefined {
        return this.registrations.get(workflowId);
    }

    /**
     * Get all registrations.
     *
     * @returns Array of all registrations
     */
    static getAllRegistrations(): WorkflowRegistration[] {
        return Array.from(this.registrations.values());
    }

    /**
     * Clear all registrations.
     * Primarily used for testing.
     */
    static clear(): void {
        this.registrations.clear();
    }
}
